import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Decimal from 'decimal.js';

import { Loader, Text } from '@hotjar/hotsauce';
import { usePrettifyPrice } from '@hotjar/webapp/hooks/usePrettifyPrice';
import type { Site } from '@hotjar/site';
import { BillingPeriodSwitch } from '@hotjar/upsell';
import type { OwnedAccount } from '@hotjar/account';
import {
  type BillingPeriod,
  type SelectedPlans,
  mapSelectedPlansToV3ProductAllowances,
  useSubscriptionInfo,
  getYearlyDiscount,
} from '@hotjar/subscription-management';
import type { ProductCatalog as ProductCatalogV3 } from '@hotjar/product-catalog';
import { getBundledPlanId } from '@hotjar/webapp/domains/common/product/utils';
import { isBillingV3Enabled } from '@hotjar/webapp/domains/billing/features';

import { LoaderWrapper } from '~/trials/components/LoaderWrapper/LoaderWrapper';
import {
  type ProductCatalog as ProductCatalogV2,
  type AnalyticsProduct,
  type FeedbackProduct,
  ProductType,
} from '~/trials/external-dependencies/domains/product/types';
import { useCreateSubscriptionOrderPreview } from '~/trials/hooks/useCreateSubscriptionOrderPreview';
import { useSitesCost } from '~/trials/hooks/useSitesCost';

import { SiteListItem } from './components/SiteListItem/SiteListItem';
import { SiteListStyles as S } from './SiteList.styles';

interface Props {
  onPlanChange: (site: Site, plan: AnalyticsProduct | FeedbackProduct) => void;
  selectedPlans: SelectedPlans;
  billingPeriod: BillingPeriod;
  onBillingPeriodChange: (billingPeriod: BillingPeriod) => void;
  isLoading: boolean;
  billingPeriodChanged: boolean;
  sites: Site[];
  productCatalogV2: ProductCatalogV2 | undefined;
  productCatalogV3: ProductCatalogV3 | undefined;
  ownedAccount: OwnedAccount | undefined;
  isAnnualBillingEligible: boolean;
  canSelfServe: boolean;
}

export const SiteList = ({
  onPlanChange,
  selectedPlans,
  billingPeriod,
  onBillingPeriodChange,
  isLoading,
  billingPeriodChanged,
  sites,
  productCatalogV2,
  productCatalogV3,
  ownedAccount,
  isAnnualBillingEligible,
  canSelfServe,
}: Props): JSX.Element => {
  const { prettifyPrice } = usePrettifyPrice();
  const { t } = useTranslation('trials', { keyPrefix: 'post-trial.site-list' });

  const selectedAllowances = useMemo(() => {
    if (!sites.length) {
      return [];
    }

    return mapSelectedPlansToV3ProductAllowances({
      sites,
      productCatalog: productCatalogV3,
      selectedPlans,
    });
  }, [productCatalogV3, selectedPlans, sites]);

  const shouldUseLegacyPrices = !isBillingV3Enabled(ownedAccount);

  const planIdsBySite = useMemo(() => {
    return Object.fromEntries(
      Object.entries(selectedPlans).map(([site, products]) => {
        const analyticsProduct = products[ProductType.Analytics];
        const feedbackProduct = products[ProductType.Feedback];

        if (!analyticsProduct || !feedbackProduct) {
          return [site, null];
        }

        return [
          site,
          getBundledPlanId({
            analyticsPlanId: analyticsProduct.sku,
            feedbackPlanId: feedbackProduct.sku,
          }),
        ];
      }),
    );
  }, [selectedPlans]);

  /**
   * @deprecated Legacy prices for billing v2 accounts
   */
  const { fetching: fetchingLegacyMonthlyCost, totalPrice: monthlyTotalLegacyPrice } = useSitesCost(
    {
      account: ownedAccount,
      planIdsBySite,
      billingPeriod: 'MONTHLY',
    },
  );

  /**
   * @deprecated Legacy prices for billing v2 accounts
   */
  const { fetching: fetchingLegacyAnnualCost, totalPrice: annualTotalLegacyPrice } = useSitesCost({
    account: ownedAccount,
    planIdsBySite,
    billingPeriod: 'ANNUALLY',
  });

  const isFetchingLegacyPrices =
    Boolean(fetchingLegacyMonthlyCost ?? true) || Boolean(fetchingLegacyAnnualCost ?? true);

  const { data: subscriptionInfo } = useSubscriptionInfo();
  const { subscription } = subscriptionInfo?.customer ?? {};
  const { data: orderPreview, isLoading: isLoadingOrderPreview } =
    useCreateSubscriptionOrderPreview({
      account: ownedAccount,
      billingPeriod,
      productAllowances: selectedAllowances,
    });

  const annualDiscount = getYearlyDiscount(
    orderPreview?.payNow.purchasedProductCosts,
    true,
    subscription,
  );
  const { amount: annualDiscountNumber } = annualDiscount ?? {};
  const annualDiscountDecimal = new Decimal(annualDiscountNumber ?? 0).abs();
  const totalCost = new Decimal(orderPreview?.payNow.amountWithoutTax ?? 0);
  const monthlyCostWithoutAnnualDiscount =
    billingPeriod === 'ANNUALLY'
      ? totalCost.dividedBy(12).add(annualDiscountDecimal.dividedBy(12))
      : totalCost;
  const monthlyCostWithAnnualDiscount =
    billingPeriod === 'ANNUALLY' ? totalCost.dividedBy(12) : totalCost;

  return (
    <S.Container>
      <Loader isLoading={isLoading} size="xl">
        {sites.length &&
          productCatalogV2 &&
          Object.keys(selectedPlans).length > 0 &&
          sites.map((site) => (
            <SiteListItem
              key={site.id}
              site={site}
              productCatalog={productCatalogV2}
              onPlanChange={onPlanChange}
              selectedProducts={selectedPlans[site.id]}
            />
          ))}
        <S.Footer>
          <S.FooterContent>
            {isAnnualBillingEligible ? (
              <BillingPeriodSwitch
                value={billingPeriod}
                onChange={onBillingPeriodChange}
                isLocked={!billingPeriodChanged && billingPeriod === 'ANNUALLY'}
              />
            ) : (
              <div />
            )}
            <S.Total>
              {canSelfServe ? (
                <>
                  {shouldUseLegacyPrices && (
                    <LoaderWrapper loading={isFetchingLegacyPrices} size="200px">
                      <S.Price
                        data-testid="post-trial-modal-site-list-total-price"
                        textType="body2"
                        textColor="text_default"
                      >
                        {billingPeriod === 'MONTHLY' ? (
                          <Trans
                            i18nKey="trials:post-trial.site-list.total-cost-per-month"
                            components={[<Text textType="subtitle1" textColor="text_default" />]}
                            values={{
                              cost: prettifyPrice(monthlyTotalLegacyPrice?.withoutDiscounts || 0),
                            }}
                          />
                        ) : (
                          <Trans
                            i18nKey="trials:post-trial.site-list.yearly-total-cost-per-month"
                            components={[
                              <Text
                                textType="body2"
                                style={{ textDecorationLine: 'line-through' }}
                                textColor="text_disabled"
                              />,
                              <Text textType="subtitle1" textColor="text_success" />,
                            ]}
                            values={{
                              monthlyBilledCostPerMonth: prettifyPrice(
                                monthlyTotalLegacyPrice?.withoutDiscounts || 0,
                              ),
                              cost: prettifyPrice(
                                annualTotalLegacyPrice?.withDiscounts.dividedBy(12) || 0,
                              ),
                            }}
                          />
                        )}
                      </S.Price>
                    </LoaderWrapper>
                  )}

                  {!shouldUseLegacyPrices && (
                    <LoaderWrapper loading={isLoadingOrderPreview} size="200px">
                      <S.Price
                        data-testid="post-trial-modal-site-list-total-price"
                        textType="body2"
                        textColor="text_default"
                      >
                        {billingPeriod === 'MONTHLY' ? (
                          <Trans
                            i18nKey="trials:post-trial.site-list.total-cost-per-month"
                            components={[<Text textType="subtitle1" textColor="text_default" />]}
                            values={{
                              cost: prettifyPrice(totalCost),
                            }}
                          />
                        ) : (
                          <Trans
                            i18nKey="trials:post-trial.site-list.yearly-total-cost-per-month"
                            components={[
                              <Text
                                textType="body2"
                                style={{ textDecorationLine: 'line-through' }}
                                textColor="text_disabled"
                              />,
                              <Text textType="subtitle1" textColor="text_success" />,
                            ]}
                            values={{
                              monthlyBilledCostPerMonth: prettifyPrice(
                                monthlyCostWithoutAnnualDiscount,
                              ),
                              cost: prettifyPrice(monthlyCostWithAnnualDiscount),
                            }}
                          />
                        )}
                      </S.Price>
                    </LoaderWrapper>
                  )}
                  {billingPeriod === 'ANNUALLY' && (
                    <Text as="p" textType="caption" textColor="text_disabled">
                      {t('yearly-billed-label')}
                    </Text>
                  )}
                </>
              ) : (
                <div>{t('total-cost-non-self-serve')}</div>
              )}
            </S.Total>
          </S.FooterContent>
          <S.FooterLabel textType="caption" textColor="text_light">
            {t('footer-disclaimer')}
          </S.FooterLabel>
        </S.Footer>
      </Loader>
    </S.Container>
  );
};
